import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  LevelFormat,
  ExternalHyperlink,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  PageBreak,
  ImageRun,
  HorizontalPositionAlign,
  HorizontalPositionRelativeFrom,
  VerticalPositionRelativeFrom,
  TextWrappingType,
  TextWrappingSide,
  Header,
  Footer,
  PageNumber,
  CommentRangeStart,
  CommentRangeEnd,
  CommentReference,
  InsertedTextRun,
  DeletedTextRun,
  ImportedXmlComponent,
  convertMillimetersToTwip,
} from 'docx';
import type { PageSetup, PageNumberSetup, DocComment } from '@/state/types';
import { PAGE_DIMENSIONS } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Export editor → DOCX (docx.js), sekce 2.4
//
// Strategie: TipTap dá HTML, my ho projdeme a poskládáme z něj docx.js
// elementy (Paragraph / TextRun) s mapováním na Word styly:
//   - <h1>–<h6> → HeadingLevel.HEADING_1..6 (w:pStyle)
//   - align → AlignmentType (w:jc)
//   - <strong>/<b>, <em>/<i>, <u>, <s>, <sub>, <sup> → run properties
//   - color / highlight / font-family z inline stylů
//   - <ul>/<ol> → číslované/odrážkové seznamy (w:numPr)
//   - <a href> → ExternalHyperlink
//
// PageSetup se promítne do section properties (velikost, orientace, okraje).
//
// Track Changes (w:ins/w:del) a komentáře (comments.xml) jsou Fáze 3 —
// zde záměrně NEřešeny. Technické riziko docx.js pro tyto prvky je ve
// specifikaci označeno k prověření na začátku Fáze 3.
// ═══════════════════════════════════════════════════════════════

interface RunStyle {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  strike?: boolean;
  subScript?: boolean;
  superScript?: boolean;
  color?: string;
  highlight?: string;
  font?: string;
  size?: number; // půlbody (pt × 2) pro docx.js
}

const HEADING_MAP: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  H1: HeadingLevel.HEADING_1,
  H2: HeadingLevel.HEADING_2,
  H3: HeadingLevel.HEADING_3,
  H4: HeadingLevel.HEADING_4,
  H5: HeadingLevel.HEADING_5,
  H6: HeadingLevel.HEADING_6,
};

const ALIGN_MAP: Record<string, (typeof AlignmentType)[keyof typeof AlignmentType]> = {
  left: AlignmentType.LEFT,
  center: AlignmentType.CENTER,
  right: AlignmentType.RIGHT,
  justify: AlignmentType.JUSTIFIED,
};

/** Normalizuje #rgb/#rrggbb/rgb() na 6místný hex bez #. */
function normalizeColor(c: string | undefined): string | undefined {
  if (!c) return undefined;
  c = c.trim();
  const rgb = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgb) {
    return [rgb[1], rgb[2], rgb[3]]
      .map((n) => parseInt(n, 10).toString(16).padStart(2, '0'))
      .join('');
  }
  const hex = c.replace('#', '');
  if (hex.length === 3) return hex.split('').map((x) => x + x).join('');
  if (hex.length === 6) return hex;
  return undefined;
}

// ── Obrázky ──────────────────────────────────────────────────
// Blok 4: <img data-*> → docx ImageRun. Data URL se dekóduje na bajty,
// wrap režim se mapuje na Word umístění:
//   inline       → ImageRun v běžném textu
//   block/left/right → floating anchor (kotvení k odstavci)
// Graceful fallback: když se floating nepovede sestavit, degradujeme na
// inline, aby export nikdy nerozbil celý dokument (flaglované riziko docx.js).

const PX_PER_INCH = 96;
const EMU_PER_INCH = 914400;
function pxToEmu(px: number): number {
  return Math.round((px / PX_PER_INCH) * EMU_PER_INCH);
}

interface DecodedImage {
  data: Uint8Array;
  type: 'png' | 'jpg' | 'gif' | 'bmp';
}

/** Dekóduje data URL (base64) na bajty + typ pro docx.js. Vrací null při selhání. */
function decodeDataUrl(src: string): DecodedImage | null {
  const m = src.match(/^data:(image\/[a-z0-9.+-]+);base64,(.*)$/i);
  if (!m) return null;
  const mime = m[1].toLowerCase();
  let type: DecodedImage['type'];
  if (mime.includes('png')) type = 'png';
  else if (mime.includes('jpeg') || mime.includes('jpg')) type = 'jpg';
  else if (mime.includes('gif')) type = 'gif';
  else if (mime.includes('bmp')) type = 'bmp';
  // SVG/WebP docx.js nepodporuje jako ImageRun typ → přeskočíme (fallback: žádný obrázek).
  else return null;

  try {
    const bin = atob(m[2]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { data: bytes, type };
  } catch {
    return null;
  }
}

/** Rozměry obrázku v px z atributů; fallback na rozumnou výchozí velikost. */
function imageDims(el: HTMLElement): { w: number; h: number } {
  let w = parseInt(el.getAttribute('width') || '', 10);
  let h = parseInt(el.getAttribute('height') || '', 10);
  if (isNaN(w) || w <= 0) w = 320;
  if (isNaN(h) || h <= 0) h = 240;
  return { w, h };
}

/** Sestaví docx ImageRun z <img>. wrap=inline → inline; block/left/right → floating. */
function imageToRun(el: HTMLElement): ImageRun | null {
  const src = el.getAttribute('src') || '';
  const decoded = decodeDataUrl(src);
  if (!decoded) return null; // nepodporovaný/nevalidní obrázek → přeskoč

  const { w, h } = imageDims(el);
  const wrap = el.getAttribute('data-wrap') || 'inline';
  const align = el.getAttribute('data-align') || 'left';

  const base = {
    data: decoded.data,
    type: decoded.type,
    transformation: { width: w, height: h },
  } as const;

  if (wrap === 'inline') {
    return new ImageRun(base as any);
  }

  // Floating (block/left/right). Když sestavení selže, spadneme na inline.
  try {
    const wrapType =
      wrap === 'left' || wrap === 'right' ? TextWrappingType.SQUARE : TextWrappingType.TOP_AND_BOTTOM;
    const wrapSide =
      wrap === 'left' ? TextWrappingSide.RIGHT : wrap === 'right' ? TextWrappingSide.LEFT : TextWrappingSide.BOTH_SIDES;

    // Vodorovné umístění: left/right float u okraje sloupce; block dle align.
    let hAlign: (typeof HorizontalPositionAlign)[keyof typeof HorizontalPositionAlign] =
      HorizontalPositionAlign.LEFT;
    if (wrap === 'right') hAlign = HorizontalPositionAlign.RIGHT;
    else if (wrap === 'block') {
      hAlign =
        align === 'center'
          ? HorizontalPositionAlign.CENTER
          : align === 'right'
            ? HorizontalPositionAlign.RIGHT
            : HorizontalPositionAlign.LEFT;
    }

    return new ImageRun({
      ...base,
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.COLUMN,
          align: hAlign,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          offset: 0,
        },
        wrap: { type: wrapType, side: wrapSide },
        margins: { left: pxToEmu(6), right: pxToEmu(6), top: pxToEmu(2), bottom: pxToEmu(2) },
      },
    } as any);
  } catch {
    // Graceful fallback — floating se nepodařil, vlož obrázek inline.
    return new ImageRun(base as any);
  }
}

/**
 * ID komentářů, které reálně existují v aktuálním exportu. Nastavuje se na
 * začátku exportDocx a čte ho collectRuns při rozhodování, jestli smí
 * vygenerovat comment range. Zabraňuje osiřelým referencím, které dělají
 * z .docx soubor, který Word odmítne otevřít.
 */
let validCommentIds: Set<number> = new Set();

/**
 * Počítadlo revizních ID pro sledované změny. Word chce, aby každý
 * <w:ins>/<w:del> měl unikátní číselné w:id v rámci dokumentu.
 * Resetuje se na začátku každého exportu.
 */
let revisionIdCounter = 1;
function nextRevisionId(): number {
  return revisionIdCounter++;
}

/**
 * Datum pro Track Changes → ISO string bez milisekund.
 *
 * docx.js má nekonzistentní typy: Track Changes chce `date` jako STRING,
 * kdežto komentáře jako Date objekt. Kdybychom sem předali Date, docx.js
 * ho zřetězí přes toString() a vznikne „Fri Jul 17 2026 …", což Word
 * odmítne. Proto normalizujeme na ISO 8601.
 */
function normalizeTrackDate(raw: string | null): string {
  const d = raw ? new Date(raw) : new Date();
  const valid = isNaN(d.getTime()) ? new Date() : d;
  return valid.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** Rekurzivně sesbírá TextRun/Hyperlink z inline uzlů odstavce. */
function collectRuns(node: Node, inherited: RunStyle): (TextRun | ExternalHyperlink | ImageRun)[] {
  const runs: (TextRun | ExternalHyperlink | ImageRun)[] = [];

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      if (text) runs.push(new TextRun({ text, ...toRunProps(inherited) }));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const el = child as HTMLElement;
    const tag = el.tagName;
    const style: RunStyle = { ...inherited };

    if (tag === 'STRONG' || tag === 'B') style.bold = true;
    if (tag === 'EM' || tag === 'I') style.italics = true;
    if (tag === 'U') style.underline = true;
    if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') style.strike = true;
    if (tag === 'SUB') style.subScript = true;
    if (tag === 'SUP') style.superScript = true;

    const color = normalizeColor(el.style.color);
    if (color) style.color = color;
    const hl = normalizeColor(el.style.backgroundColor);
    if (hl) style.highlight = hl;
    if (el.style.fontFamily) style.font = el.style.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
    if (el.style.fontSize) {
      const pt = parseFloat(el.style.fontSize); // '14pt' → 14
      if (!isNaN(pt)) style.size = Math.round(pt * 2); // docx.js chce půlbody
    }

    if (tag === 'A') {
      const href = el.getAttribute('href') || '';
      const inner = collectRuns(el, style).filter((r): r is TextRun => r instanceof TextRun);
      runs.push(new ExternalHyperlink({ children: inner.length ? inner : [new TextRun({ text: el.textContent || href, ...toRunProps(style) })], link: href }));
      return;
    }

    if (tag === 'BR') {
      runs.push(new TextRun({ text: '', break: 1 }));
      return;
    }

    if (tag === 'IMG') {
      const run = imageToRun(el);
      if (run) runs.push(run);
      return;
    }

    // Token čísla stránky (<span data-field="page|pages">) → docx field run.
    if (el.hasAttribute('data-field')) {
      const kind = el.getAttribute('data-field');
      if (kind === 'pages') {
        runs.push(new TextRun({ children: [PageNumber.TOTAL_PAGES], ...toRunProps(style) }));
      } else {
        runs.push(new TextRun({ children: [PageNumber.CURRENT], ...toRunProps(style) }));
      }
      return;
    }

    // Sledované změny (Fáze 3) — <span data-track-ins/del> → w:ins / w:del.
    // Text uvnitř sesbíráme a vyrobíme z něj InsertedTextRun/DeletedTextRun,
    // které docx.js zabalí do <w:ins>/<w:del> s autorem a časem. Word je pak
    // zobrazí ve svém review panelu.
    //
    // POZOR na typy docx.js: Track Changes chce `date` jako ISO STRING
    // (na rozdíl od komentářů, které chtějí Date objekt). Předání Date sem
    // by vygenerovalo nevalidní w:date a Word by to odmítl.
    if (el.hasAttribute('data-track-ins') || el.hasAttribute('data-track-del')) {
      const isIns = el.hasAttribute('data-track-ins');
      const author = el.getAttribute('data-author') || 'Neznámý autor';
      const date = normalizeTrackDate(el.getAttribute('data-date'));
      const text = el.textContent || '';
      if (text) {
        const opts = {
          text,
          id: nextRevisionId(),
          author,
          date,
          ...toRunProps(style),
        };
        runs.push((isIns ? new InsertedTextRun(opts as any) : new DeletedTextRun(opts as any)) as any);
      }
      return;
    }

    // Komentovaný rozsah (<span data-comment-id="N">) → Word comment range.
    // Obalíme runy uvnitř značkami commentRangeStart/End a za ně dáme
    // referenci, přesně jak to čeká Word.
    //
    // KRITICKÉ: referenci vygenerujeme JEN když komentář daného ID reálně
    // existuje v comments.xml. Osiřelá reference (mark zůstal v textu po
    // smazání komentáře) způsobí, že Word soubor odmítne otevřít jako
    // poškozený. Když komentář neexistuje, mark prostě ignorujeme a obsah
    // vyexportujeme jako běžný text.
    if (el.hasAttribute('data-comment-id')) {
      const cid = parseInt(el.getAttribute('data-comment-id') || '', 10);
      if (!isNaN(cid) && validCommentIds.has(cid)) {
        runs.push(new CommentRangeStart(cid) as any);
        runs.push(...collectRuns(el, style));
        runs.push(new CommentRangeEnd(cid) as any);
        runs.push(new TextRun({ children: [new CommentReference(cid)] }));
        return;
      }
      // Neplatné/osiřelé ID → jen obsah, bez značek komentáře.
      runs.push(...collectRuns(el, style));
      return;
    }

    runs.push(...collectRuns(el, style));
  });

  return runs;
}

function toRunProps(s: RunStyle) {
  return {
    bold: s.bold,
    italics: s.italics,
    underline: s.underline ? {} : undefined,
    strike: s.strike,
    subScript: s.subScript,
    superScript: s.superScript,
    color: s.color,
    highlight: s.highlight ? colorToHighlightName(s.highlight) : undefined,
    font: s.font,
    size: s.size,
  };
}

/** docx highlight bere pojmenované barvy; mapujeme nejbližší. Fallback yellow. */
type HighlightColor =
  | 'yellow' | 'green' | 'cyan' | 'magenta' | 'red' | 'blue' | 'black' | 'white';
function colorToHighlightName(hex: string): HighlightColor {
  const named: Record<string, HighlightColor> = {
    ffff00: 'yellow', '00ff00': 'green', '00ffff': 'cyan', 'ff00ff': 'magenta',
    ff0000: 'red', '0000ff': 'blue', '000000': 'black', ffffff: 'white',
  };
  return named[hex.toLowerCase()] || 'yellow';
}

function alignFromEl(el: HTMLElement): (typeof AlignmentType)[keyof typeof AlignmentType] | undefined {
  const a = el.style.textAlign || el.getAttribute('data-text-align') || '';
  return ALIGN_MAP[a];
}

/** Převede jeden blokový element na docx Paragraph(y) nebo Table. */
function blockToParagraphs(el: HTMLElement): (Paragraph | Table)[] {
  const tag = el.tagName;

  if (tag === 'TABLE') {
    return [tableToDocx(el)];
  }

  if (tag === 'DIV' && el.getAttribute('data-page-break') !== null) {
    return [new Paragraph({ children: [new PageBreak()] })];
  }

  if (HEADING_MAP[tag]) {
    // Word aplikuje na nadpisové styly vlastní (modrou) barvu z šablony.
    // Aby nadpis vypadal jako v editoru (černý, pokud uživatel neurčil jinak),
    // vnutíme runům explicitní barvu, když žádnou nemají.
    return [new Paragraph({
      heading: HEADING_MAP[tag],
      alignment: alignFromEl(el),
      children: collectRuns(el, { color: '000000' }),
    })];
  }

  if (tag === 'UL' || tag === 'OL') {
    const ordered = tag === 'OL';
    const items: Paragraph[] = [];
    el.querySelectorAll(':scope > li').forEach((li) => {
      items.push(new Paragraph({
        children: collectRuns(li, {}),
        numbering: ordered ? { reference: 'numbered', level: 0 } : undefined,
        bullet: ordered ? undefined : { level: 0 },
      }));
    });
    return items;
  }

  if (tag === 'BLOCKQUOTE') {
    return [new Paragraph({ children: collectRuns(el, {}), indent: { left: convertMillimetersToTwip(12) } })];
  }

  // default: odstavec
  return [new Paragraph({ alignment: alignFromEl(el), children: collectRuns(el, {}) })];
}

/** Převede <table> na docx Table s ohraničením. */
function tableToDocx(tableEl: HTMLElement): Table {
  const rows: TableRow[] = [];
  const border = { style: BorderStyle.SINGLE, size: 4, color: '9AA0A8' };
  const cellBorders = { top: border, bottom: border, left: border, right: border };

  tableEl.querySelectorAll(':scope > tbody > tr, :scope > thead > tr, :scope > tr').forEach((tr) => {
    const cells: TableCell[] = [];
    tr.querySelectorAll(':scope > td, :scope > th').forEach((cell) => {
      const c = cell as HTMLElement;
      const colspan = parseInt(c.getAttribute('colspan') || '1', 10);
      const rowspan = parseInt(c.getAttribute('rowspan') || '1', 10);
      const isHeader = c.tagName === 'TH';

      // Obsah buňky: každý blokový potomek → Paragraph; jinak celý text.
      const paras: Paragraph[] = [];
      const blockChildren = c.querySelectorAll(':scope > p, :scope > h1, :scope > h2, :scope > h3');
      if (blockChildren.length) {
        blockChildren.forEach((b) => paras.push(new Paragraph({ children: collectRuns(b as HTMLElement, isHeader ? { bold: true } : {}) })));
      } else {
        paras.push(new Paragraph({ children: collectRuns(c, isHeader ? { bold: true } : {}) }));
      }

      cells.push(new TableCell({
        children: paras.length ? paras : [new Paragraph({ children: [] })],
        columnSpan: colspan > 1 ? colspan : undefined,
        rowSpan: rowspan > 1 ? rowspan : undefined,
        borders: cellBorders,
        shading: isHeader ? { fill: 'F0F2F5' } : undefined,
      }));
    });
    if (cells.length) rows.push(new TableRow({ children: cells }));
  });

  return new Table({
    rows: rows.length ? rows : [new TableRow({ children: [new TableCell({ children: [new Paragraph({ children: [] })] })] })],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
}

/** Section properties z PageSetup. */
function sectionProps(page: PageSetup) {
  const dims = PAGE_DIMENSIONS[page.size];
  const landscape = page.orientation === 'landscape';
  const w = landscape ? dims.h : dims.w;
  const h = landscape ? dims.w : dims.h;
  return {
    page: {
      size: {
        width: convertMillimetersToTwip(w),
        height: convertMillimetersToTwip(h),
        orientation: landscape ? ('landscape' as const) : ('portrait' as const),
      },
      margin: {
        top: convertMillimetersToTwip(page.margins.top),
        right: convertMillimetersToTwip(page.margins.right),
        bottom: convertMillimetersToTwip(page.margins.bottom),
        left: convertMillimetersToTwip(page.margins.left),
      },
    },
  };
}

/** Vstup exportu — tělo, nastavení stránky, záhlaví/zápatí, číslo stránky. */
export interface ExportInput {
  body: string;
  page: PageSetup;
  header?: string;
  footer?: string;
  pageNumber?: PageNumberSetup;
  /** Komentáře dokumentu (Fáze 3) — vazba na text je přes data-comment-id. */
  comments?: DocComment[];
}

/**
 * Sestaví docx Header/Footer z HTML panelu. Když je panel prázdný a zároveň
 * má sem patřit automatické číslo stránky, vygeneruje se odstavec jen s ním.
 * Vrací objekt {default} nebo undefined (žádné záhlaví/zápatí se negeneruje).
 */
function buildHeaderFooter(
  html: string | undefined,
  pageNumber: PageNumberSetup | undefined,
  which: 'header' | 'footer',
): { default: Header | Footer } | undefined {
  const dom = new DOMParser().parseFromString(html || '', 'text/html');
  const hasContent = !!dom.body.textContent?.trim() || dom.body.querySelector('img, [data-field]');

  // Má sem patřit automatické číslo stránky (když uživatel zapnul volbu a
  // panel sám o sobě žádné pole čísla neobsahuje)?
  const wantsAutoNumber =
    pageNumber?.enabled && pageNumber.place === which && !dom.body.querySelector('[data-field]');

  if (!hasContent && !wantsAutoNumber) return undefined;

  const children: Paragraph[] = [];

  dom.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // V záhlaví/zápatí bereme jen odstavcové bloky (nadpisy/tabulky tu
      // nedávají smysl a Word je stejně mapuje na odstavce).
      children.push(
        new Paragraph({
          alignment: alignFromEl(el),
          children: collectRuns(el, {}) as (TextRun | ExternalHyperlink)[],
        }),
      );
    }
  });

  // Doplň automatické číslo stránky, pokud je požadované a panel ho neměl.
  if (wantsAutoNumber) {
    const align = ALIGN_MAP[pageNumber!.align] || AlignmentType.CENTER;
    children.push(
      new Paragraph({
        alignment: align,
        children: [new TextRun({ children: [PageNumber.CURRENT] })],
      }),
    );
  }

  if (!children.length) children.push(new Paragraph({ children: [] }));

  return which === 'header'
    ? { default: new Header({ children }) }
    : { default: new Footer({ children }) };
}

/**
 * Sestaví strukturu komentářů pro docx.js. Vrací undefined, když žádné
 * komentáře nejsou (aby dokument zbytečně nenesl prázdný comments.xml).
 *
 * Vlákna: `parentId` ukazuje na kořen vlákna → docx.js z toho udělá
 * w15:paraIdParent v commentsExtended.xml.
 * Vyřešení: `resolved` → w15:done="1".
 */
function buildComments(comments: DocComment[] | undefined) {
  if (!comments || !comments.length) return undefined;
  return {
    children: comments.map((c) => ({
      id: c.id,
      author: c.author,
      initials: c.initials,
      // Comment API chce Date objekt (na rozdíl od Track Changes, které chce
      // ISO string) — proto převod. Neplatné datum by shodilo export.
      date: new Date(c.date),
      children: [new Paragraph({ children: [new TextRun(c.text)] })],
      ...(c.parentId != null ? { parentId: c.parentId } : {}),
      ...(c.resolved ? { resolved: true } : {}),
    })),
  };
}

/**
 * Kompletní sada výchozích stylů odpovídající tomu, co zapisuje Word.
 *
 * Proč ručně přes OOXML: potřebujeme na stylu „Normal" příznak
 * `w:default="1"` — bez něj Word použije SVŮJ vestavěný Normal (jiné
 * řádkování a mezery) a dokument po round-tripu vypadá jinak než originál.
 * docx.js ten příznak přes `paragraphStyles` nastavit neumí.
 *
 * POZOR: `importedStyles` NAHRAZUJE celou sadu stylů, kterou by docx.js
 * vygeneroval sám — proto tu musí být i definice nadpisů. Kdyby chyběly,
 * <w:pStyle w:val="Heading1"/> v textu by ukazoval na neexistující styl
 * a nadpisy by se zobrazily jako obyčejný text.
 *
 * Hodnoty (16pt/13pt/12pt, modrá 2E74B5) odpovídají výchozí šabloně Wordu.
 */
function buildDefaultStyles() {
  const heading = (id: string, name: string, sizeHalfPt: number, outline: number) =>
    `<w:style w:type="paragraph" w:styleId="${id}">` +
    `<w:name w:val="${name}"/>` +
    `<w:basedOn w:val="Normal"/>` +
    `<w:next w:val="Normal"/>` +
    `<w:qFormat/>` +
    `<w:pPr><w:keepNext/><w:keepLines/>` +
    `<w:spacing w:before="240" w:after="0"/>` +
    `<w:outlineLvl w:val="${outline}"/></w:pPr>` +
    `<w:rPr><w:rFonts w:asciiTheme="majorHAnsi" w:hAnsiTheme="majorHAnsi"/>` +
    `<w:color w:val="2E74B5"/><w:sz w:val="${sizeHalfPt}"/><w:szCs w:val="${sizeHalfPt}"/></w:rPr>` +
    `</w:style>`;

  const xml =
    // Výchozí vlastnosti dokumentu. importedStyles nahrazuje CELÝ obsah
    // styles.xml (včetně toho, co by docx.js vygenerovalo z `styles.default`),
    // takže docDefaults musí být tady — jinak by ve stylech úplně chyběly.
    '<w:docDefaults>' +
      '<w:rPrDefault><w:rPr>' +
        '<w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>' +
        '<w:sz w:val="22"/><w:szCs w:val="22"/>' +
      '</w:rPr></w:rPrDefault>' +
      '<w:pPrDefault><w:pPr>' +
        '<w:spacing w:after="0" w:line="240" w:lineRule="auto"/>' +
      '</w:pPr></w:pPrDefault>' +
    '</w:docDefaults>' +
    // Výchozí odstavcový styl — klíčový je w:default="1".
    '<w:style w:type="paragraph" w:default="1" w:styleId="Normal">' +
      '<w:name w:val="Normal"/><w:qFormat/>' +
      '<w:pPr><w:spacing w:after="0" w:before="0" w:line="240" w:lineRule="auto"/></w:pPr>' +
      '<w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>' +
      '<w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr>' +
    '</w:style>' +
    // Výchozí znakový styl (Word ho očekává).
    '<w:style w:type="character" w:default="1" w:styleId="DefaultParagraphFont">' +
      '<w:name w:val="Default Paragraph Font"/><w:uiPriority w:val="1"/>' +
      '<w:semiHidden/><w:unhideWhenUsed/>' +
    '</w:style>' +
    // Nadpisy 1–6 (velikosti dle výchozí šablony Wordu).
    heading('Heading1', 'heading 1', 32, 0) +
    heading('Heading2', 'heading 2', 26, 1) +
    heading('Heading3', 'heading 3', 24, 2) +
    heading('Heading4', 'heading 4', 22, 3) +
    heading('Heading5', 'heading 5', 22, 4) +
    heading('Heading6', 'heading 6', 22, 5) +
    // Titulek a podtitulek.
    '<w:style w:type="paragraph" w:styleId="Title">' +
      '<w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/>' +
      '<w:pPr><w:spacing w:after="80" w:line="240" w:lineRule="auto"/><w:contextualSpacing/></w:pPr>' +
      '<w:rPr><w:rFonts w:asciiTheme="majorHAnsi" w:hAnsiTheme="majorHAnsi"/>' +
      '<w:spacing w:val="-10"/><w:kern w:val="28"/><w:sz w:val="56"/><w:szCs w:val="56"/></w:rPr>' +
    '</w:style>' +
    // Styl odstavce v seznamu — docx.js ho používá u odrážek/číslování.
    '<w:style w:type="paragraph" w:styleId="ListParagraph">' +
      '<w:name w:val="List Paragraph"/><w:basedOn w:val="Normal"/><w:uiPriority w:val="34"/>' +
      '<w:qFormat/><w:pPr><w:ind w:left="720"/><w:contextualSpacing/></w:pPr>' +
    '</w:style>' +
    // Hypertextový odkaz.
    '<w:style w:type="character" w:styleId="Hyperlink">' +
      '<w:name w:val="Hyperlink"/><w:basedOn w:val="DefaultParagraphFont"/>' +
      '<w:uiPriority w:val="99"/><w:unhideWhenUsed/>' +
      '<w:rPr><w:color w:val="0563C1"/><w:u w:val="single"/></w:rPr>' +
    '</w:style>' +
    // Styly komentářů (Word je očekává, když dokument komentáře obsahuje).
    '<w:style w:type="paragraph" w:styleId="CommentText">' +
      '<w:name w:val="annotation text"/><w:basedOn w:val="Normal"/>' +
      '<w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/>' +
      '<w:rPr><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr>' +
    '</w:style>' +
    '<w:style w:type="character" w:styleId="CommentReference">' +
      '<w:name w:val="annotation reference"/><w:basedOn w:val="DefaultParagraphFont"/>' +
      '<w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/>' +
      '<w:rPr><w:sz w:val="16"/><w:szCs w:val="16"/></w:rPr>' +
    '</w:style>';

  // importedStyles čeká pole komponent — rozdělíme XML na jednotlivé bloky
  // (docDefaults + jednotlivé styly).
  //
  // POZOR: ImportedXmlComponent.fromXmlString() vrací OBAL s rootKey
  // `undefined`, uvnitř kterého je teprve skutečný element. Kdybychom
  // vložili obal, do styles.xml se zapíše nesmyslný element <undefined>
  // a Word ohlásí poškozený soubor („Errors were detected… Styles 1").
  // Proto vytahujeme vnitřní komponentu (root[0]).
  const parts = xml.match(/<w:docDefaults>[\s\S]*?<\/w:docDefaults>|<w:style[\s\S]*?<\/w:style>/g) || [];
  return parts.map((p) => {
    const wrapper = ImportedXmlComponent.fromXmlString(p) as any;
    return (wrapper.root?.[0] ?? wrapper) as ImportedXmlComponent;
  });
}

/** Hlavní export: strukturovaný vstup → DOCX Blob. */
export async function exportDocx(input: ExportInput): Promise<Blob> {
  const { body, page } = input;

  // Zaregistruj platná ID komentářů PŘED zpracováním těla — collectRuns podle
  // nich rozhoduje, jestli smí vygenerovat comment range (viz validCommentIds).
  validCommentIds = new Set((input.comments || []).map((c) => c.id));
  // Revizní ID musí být unikátní v rámci dokumentu — reset na každý export.
  revisionIdCounter = 1;

  const dom = new DOMParser().parseFromString(body, 'text/html');
  const blocks: (Paragraph | Table)[] = [];

  dom.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      blocks.push(...blockToParagraphs(node as HTMLElement));
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      blocks.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
    }
  });

  if (!blocks.length) blocks.push(new Paragraph({ children: [] }));

  // Záhlaví a zápatí (Fáze 2). Prázdné panely (jen prázdný odstavec) se
  // negenerují, aby dokument zbytečně nenesl prázdné sekce.
  const headers = buildHeaderFooter(input.header, input.pageNumber, 'header');
  const footers = buildHeaderFooter(input.footer, input.pageNumber, 'footer');

  const doc = new Document({
    // Komentáře (Fáze 3). docx.js z nich vygeneruje word/comments.xml a
    // (u vláken/vyřešení) word/commentsExtended.xml.
    // POZOR na typy: Comment chce `date` jako Date objekt, kdežto Track
    // Changes API chce string — docx.js to má nekonzistentní.
    comments: buildComments(input.comments),
    // Výchozí písmo dokumentu — text bez vlastního fontu/velikosti dostane
    // Times New Roman 11 (22 půlbodů), takže se ve Wordu i jinde zobrazí
    // konzistentně a nedosazuje se aplikační default.
    styles: {
      default: {
        document: {
          run: { font: 'Times New Roman', size: 22 },
        },
      },
      // Výchozí styl „Normal" musí mít ve styles.xml příznak w:default="1",
      // jinak Word na normální odstavce použije SVŮJ vestavěný Normal (jiné
      // řádkování a mezery) a dokument po round-tripu vypadá jinak než
      // originál. docx.js tenhle příznak přes paragraphStyles nastavit neumí,
      // proto styl vkládáme jako hotové OOXML přes importedStyles.
      importedStyles: buildDefaultStyles(),
    },
    numbering: {
      config: [
        {
          reference: 'numbered',
          levels: [
            { level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.START },
          ],
        },
      ],
    },
    sections: [{ properties: sectionProps(page), headers, footers, children: blocks }],
  });

  return await Packer.toBlob(doc);
}
